import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import { 
  MagnifyingGlassIcon, 
  FileText, 
  GridNineIcon, 
  Shield, 
  User, 
  Briefcase,
  Play,
  GridFour,
} from '@phosphor-icons/react';
import { templateApi } from '../services/api';
import { useToastActions } from '../components/ui/Toast';
import type { Template, TemplateCategory, IndustryCategory } from '../types/template';

// 分类图标映射
const categoryIcons: Record<string, React.ReactNode> = {
  grid: <GridNineIcon className="w-4 h-4" />,
  shield: <Shield className="w-4 h-4" />,
  user: <User className="w-4 h-4" />,
  briefcase: <Briefcase className="w-4 h-4" />,
};

// 行业颜色映射
const industryColors: Record<string, string> = {
  retail: 'bg-blue-50 text-blue-600 border-blue-100',
  finance: 'bg-emerald-50 text-emerald-600 border-emerald-100',
  hr: 'bg-purple-50 text-purple-600 border-purple-100',
  logistics: 'bg-amber-50 text-amber-600 border-amber-100',
  manufacturing: 'bg-rose-50 text-rose-600 border-rose-100',
  education: 'bg-cyan-50 text-cyan-600 border-cyan-100',
};

export function TemplateListPage() {
  const navigate = useNavigate();
  const toast = useToastActions();
  const { t } = useTranslation();
  
  const [templates, setTemplates] = useState<Template[]>([]);
  const [categories, setCategories] = useState<TemplateCategory[]>([]);
  const [industries, setIndustries] = useState<IndustryCategory[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [selectedIndustry, setSelectedIndustry] = useState('');

  // 加载数据
  useEffect(() => {
    loadTemplates();
    loadCategories();
  }, []);

  const loadTemplates = async () => {
    try {
      setIsLoading(true);
      const response = await templateApi.listTemplates();
      if (response.code === 0) {
        setTemplates(response.data.templates);
      }
    } catch (error) {
      toast.error(t('common.error_loading'));
    } finally {
      setIsLoading(false);
    }
  };

  const loadCategories = async () => {
    try {
      const response = await templateApi.getCategories();
      if (response.code === 0) {
        setCategories(response.data.categories);
        setIndustries(response.data.industries);
      }
    } catch (error) {
      console.error('Failed to load categories', error);
    }
  };

  // 过滤模板
  const filteredTemplates = templates.filter((template) => {
    // 分类过滤
    if (selectedCategory !== 'all' && template.category !== selectedCategory) {
      return false;
    }
    // 行业过滤
    if (selectedIndustry && template.industry !== selectedIndustry) {
      return false;
    }
    // 搜索过滤
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      return (
        template.name.toLowerCase().includes(query) ||
        template.description?.toLowerCase().includes(query) ||
        template.tags?.some((tag) => tag.toLowerCase().includes(query))
      );
    }
    return true;
  });

  // 使用模板
  const handleUseTemplate = (template: Template) => {
    navigate(`/templates/${template.id}/apply`);
  };

  return (
    <div className="h-full flex flex-col bg-zinc-50">
      {/* 页面头部 */}
      <div className="bg-white border-b border-zinc-200 px-6 py-4">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-xl font-semibold text-zinc-900">{t('templates.title')}</h1>
            <p className="text-sm text-zinc-500 mt-1">
              {t('templates.subtitle')}
            </p>
          </div>
        </div>

        {/* 搜索和过滤 */}
        <div className="flex items-center gap-4 mt-4">
          {/* 搜索框 */}
          <div className="relative flex-1 max-w-md">
            <MagnifyingGlassIcon className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-400" />
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder={t('templates.searchPlaceholder')}
              className="w-full pl-10 pr-4 py-2 bg-zinc-50 border border-zinc-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500/20 focus:border-emerald-500"
            />
          </div>

          {/* 行业过滤 */}
          <select
            value={selectedIndustry}
            onChange={(e) => setSelectedIndustry(e.target.value)}
            className="px-4 py-2 bg-white border border-zinc-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500/20 focus:border-emerald-500"
          >
            <option value="">{t('templates.allIndustries')}</option>
            {industries.map((industry) => (
              <option key={industry.id} value={industry.id}>
                {industry.name}
              </option>
            ))}
          </select>
        </div>

        {/* 分类标签 */}
        <div className="flex items-center gap-2 mt-4">
          {categories.map((category) => (
            <button
              key={category.id}
              onClick={() => setSelectedCategory(category.id)}
              className={`flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-medium transition-all ${
                selectedCategory === category.id
                  ? 'bg-zinc-900 text-white'
                  : 'bg-zinc-100 text-zinc-600 hover:bg-zinc-200'
              }`}
            >
              {categoryIcons[category.icon] || <GridFour className="w-4 h-4" />}
              {category.name}
            </button>
          ))}
        </div>
      </div>

      {/* 模板列表 */}
      <div className="flex-1 overflow-auto p-6">
        {isLoading ? (
          <div className="flex items-center justify-center h-full">
            <div className="animate-spin w-8 h-8 border-2 border-zinc-200 border-t-emerald-500 rounded-full" />
          </div>
        ) : filteredTemplates.length === 0 ? (
          <div className="flex flex-col items-center justify-center h-full text-center">
            <div className="w-16 h-16 rounded-2xl bg-zinc-100 flex items-center justify-center mb-4">
              <FileText className="w-8 h-8 text-zinc-400" />
            </div>
            <h3 className="text-lg font-medium text-zinc-900">{t('templates.noTemplates')}</h3>
            <p className="text-sm text-zinc-500 mt-1">
              {searchQuery ? t('templates.noSearchResults') : t('templates.noTemplates')}
            </p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-5">
            <AnimatePresence mode="popLayout">
              {filteredTemplates.map((template) => (
                <motion.div
                  key={template.id}
                  layout
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, scale: 0.95 }}
                >
                  <TemplateCard
                    template={template}
                    onUse={handleUseTemplate}
                    t={t}
                  />
                </motion.div>
              ))}
            </AnimatePresence>
          </div>
        )}
      </div>
    </div>
  );
}

// 模板卡片组件
function TemplateCard({
  template,
  onUse,
  t,
}: {
  template: Template;
  onUse: (template: Template) => void;
  t: (key: string) => string;
}) {
  return (
    <motion.div
      layout
      whileHover={{ y: -4, boxShadow: '0 12px 40px -12px rgba(0,0,0,0.15)' }}
      className="group relative bg-white rounded-2xl border border-zinc-200 overflow-hidden transition-all duration-300"
    >
      {/* 卡片头部 */}
      <div className="relative h-24 bg-gradient-to-br from-zinc-50 to-zinc-100 p-5">
        <div className="flex items-start gap-4">
          <div className="w-12 h-12 rounded-xl bg-white shadow-sm border border-zinc-100 flex items-center justify-center">
            <FileText className="w-6 h-6 text-zinc-400" weight="duotone" />
          </div>
          <div className="flex-1 min-w-0">
            <h3 className="font-semibold text-zinc-900 truncate pr-6">{template.name}</h3>
            <p className="text-xs text-zinc-500 mt-0.5">
              {template.fields.length} {t('templates.fields')} · {t('templates.used')} {template.usageCount} {t('templates.times')}
            </p>
          </div>
        </div>
      </div>

      {/* 卡片内容 */}
      <div className="p-5">
        <p className="text-sm text-zinc-600 line-clamp-2 h-10">
          {template.description || t('templates.noDescription')}
        </p>

        {/* 标签 */}
        <div className="flex flex-wrap gap-1.5 mt-4">
          {template.industry && (
            <span className={`px-2 py-0.5 text-xs font-medium rounded-full border ${industryColors[template.industry] || 'bg-zinc-50 text-zinc-600 border-zinc-100'}`}>
              {template.industry}
            </span>
          )}
          {template.tags?.slice(0, 2).map((tag) => (
            <span
              key={tag}
              className="px-2 py-0.5 text-xs font-medium rounded-full bg-zinc-50 text-zinc-600 border border-zinc-100"
            >
              {tag}
            </span>
          ))}
          {template.category === 'system' && (
            <span className="px-2 py-0.5 text-xs font-medium rounded-full bg-emerald-50 text-emerald-600 border border-emerald-100">
              {t('templates.system')}
            </span>
          )}
        </div>

        {/* 操作按钮 */}
        <div className="flex items-center gap-2 mt-5">
          <button
            onClick={() => onUse(template)}
            className="flex-1 flex items-center justify-center gap-1.5 px-4 py-2 bg-zinc-900 text-white text-sm font-medium rounded-xl hover:bg-zinc-800 transition-colors"
          >
            <Play className="w-4 h-4" weight="fill" />
            {t('templates.useTemplate')}
          </button>
        </div>
      </div>
    </motion.div>
  );
}

export default TemplateListPage;
